/**
 * Роли/реалы приложения: admin, user, tester.
 * URL: admin.{base} — админ, test.{base} — тестер, {base} — пользователь.
 * Разные сессии (токены), разные секции навигации.
 */
export const REALMS = {
  admin: 'admin',
  user: 'user',
  tester: 'tester',
}

export const ADMIN_ROLES = ['superadmin', 'admin']
// Released self-service remains owner-only; staff and unrelated roles are excluded.
export const ONBOARDING_ROLES = [...ADMIN_ROLES, 'agency']
export const TESTER_OR_ADMIN_ROLES = ['superadmin', 'admin', 'tester', 'tester_no_media', 'tester_plus']
export const ANY_AUTHENTICATED_ROLES = [
  'superadmin',
  'admin',
  'manager',
  'user',
  'agency',
  'tester',
  'tester_no_media',
  'tester_plus',
]
export const MANAGER_ROLES = ['superadmin', 'admin', 'manager', 'user', 'agency']

export const REALM_TOKEN_KEYS = {
  admin: 'admin_token',
  user: 'user_token',
  tester: 'tester_token',
}

export function canonicalRealmForRole(role) {
  const normalized = String(role || '').toLowerCase()
  if (ADMIN_ROLES.includes(normalized)) return REALMS.admin
  if (normalized === 'tester' || normalized === 'tester_no_media' || normalized === 'tester_plus') return REALMS.tester
  if (normalized === 'manager' || normalized === 'user' || normalized === 'agency') {
    return REALMS.user
  }
  return Object.values(REALMS).includes(normalized) ? normalized : REALMS.user
}

/** Путь логина одинаковый для всех — реал определяется по домену. */
export const REALM_LOGIN_PATHS = {
  admin: '/login',
  user: '/login',
  tester: '/login',
}

/** Базовый путь для редиректа после логина — везде корень. */
export const REALM_BASE_PATHS = {
  admin: '/',
  user: '/',
  tester: '/',
}

/**
 * Определяет realm по hostname: admin.domain.com → admin, test.domain.com → tester, domain.com → user.
 * Fallback: по pathname для localhost (admin.localhost может не резолвиться).
 */
export function getRealmFromHostname(hostname = '') {
  const h = (hostname || '').toLowerCase()
  if (h.startsWith('admin.')) return 'admin'
  if (h.startsWith('test.')) return 'tester'
  return 'user'
}

/** Используется для определения realm: hostname, с fallback на pathname для localhost. */
export function getRealm(pathname = '', hostname = '') {
  const host = hostname || (typeof window !== 'undefined' ? window.location?.hostname : '')
  const path = pathname || (typeof window !== 'undefined' ? window.location?.pathname : '')
  const isDev = Boolean(import.meta.env?.DEV)

  // Development: localhost, 127.x, или devu
  if (isDev && (host === 'localhost' || host.startsWith('127.') || host === 'devu')) {
    if (path.startsWith('/admin')) return 'admin'
    if (path.startsWith('/test')) return 'tester'
  }

  return getRealmFromHostname(host)
}

export function getStoredAuthRole() {
  const value = (
    (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_role') : '') ||
    (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('auth_role') : '') ||
    ''
  ).toLowerCase()
  return value || getRealm()
}

export function isAdminRole(role = getStoredAuthRole()) {
  return ADMIN_ROLES.includes(String(role || '').toLowerCase())
}

export function isTesterOrAdminRole(role = getStoredAuthRole()) {
  return TESTER_OR_ADMIN_ROLES.includes(String(role || '').toLowerCase())
}

export function isAnyAuthenticatedRole(role = getStoredAuthRole()) {
  return ANY_AUTHENTICATED_ROLES.includes(String(role || '').toLowerCase())
}

export function isManagerRole(role = getStoredAuthRole()) {
  return MANAGER_ROLES.includes(String(role || '').toLowerCase())
}

export function defaultPathForRole(role = getStoredAuthRole()) {
  const normalized = String(role || '').toLowerCase()
  if (normalized === 'agency') return '/v2/agency-settings'
  if (normalized === 'tester' || normalized === 'tester_no_media' || normalized === 'tester_plus') return '/v2/fans'
  return '/v2'
}

/** Обратная совместимость: ранее использовали path. */
export function getRealmFromPath(pathname) {
  return getRealm(pathname, typeof window !== 'undefined' ? window.location?.hostname : '')
}

/**
 * Полный URL для перехода в другой реал (для ссылок «Войти как админ» и т.п.).
 */
export function getRealmUrl(realm, path = '/') {
  if (typeof window === 'undefined') return path
  const { hostname, port, protocol } = window.location
  const isDev = Boolean(import.meta.env?.DEV)
  const portSuffix = port && !['80', '443'].includes(port) ? `:${port}` : ''
  if (isDev && (hostname === 'localhost' || hostname.startsWith('127.') || hostname === 'devu')) {
    const prefix = realm === 'admin' ? '/admin' : realm === 'tester' ? '/test' : ''
    return `${protocol}//${hostname}${portSuffix}${prefix}${path}`
  }
  const baseHost = hostname.replace(/^(admin|test)\./, '')
  const sub = realm === 'admin' ? 'admin.' : realm === 'tester' ? 'test.' : ''
  return `${protocol}//${sub}${baseHost}${portSuffix}${path}`
}

/**
 * Очищает токены других реалов при входе в текущий.
 * @param {string} realm
 */
export function clearOtherRealmTokens(realm) {
  const canonicalRealm = canonicalRealmForRole(realm)
  const keys = Object.values(REALM_TOKEN_KEYS).filter(
    (k) => k !== REALM_TOKEN_KEYS[canonicalRealm]
  )
  keys.forEach((k) => {
    localStorage.removeItem(k)
    sessionStorage.removeItem(k)
  })
}
